import type { OpenPotDto } from '@billda/api';
import type { PotStatus } from '../../finance/types/pot.types';

/** The six statuses this widget surfaces — pots that need an admin action. */
export type ActionPotStatus =
  | 'Verifying'
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'MissedSubmitDeadline'
  | 'MissedVerificationDeadline';

export const ACTION_STATUSES: ActionPotStatus[] = [
  'Verifying',
  'Submitted',
  'Approved',
  'Rejected',
  'MissedSubmitDeadline',
  'MissedVerificationDeadline',
];

export const POT_NEXT_ACTION: Record<ActionPotStatus, string> = {
  Verifying: 'Supervisor to approve allocations',
  Submitted: 'Admin to approve for payroll',
  Approved: 'Ready for payroll to mark paid',
  Rejected: 'Owner to resolve rejections',
  MissedSubmitDeadline: 'Move to current period to reopen',
  MissedVerificationDeadline: 'Needs manual resolution — deadline passed',
};

/**
 * Plain-language next step for a pot's status. Mirrors potStatusConfig.ts's
 * getStatusColor/getStatusLabel pattern: narrow to the lookup's key type with
 * a safe fallback, since the DTO's status field carries the full PotStatus
 * union but only the six action statuses have a next-action entry.
 */
export function getNextAction(status: PotStatus): string {
  return POT_NEXT_ACTION[status as ActionPotStatus] ?? '';
}

export interface OpenPotsWeekGroup {
  periodYear: number;
  periodWeekNumber: number;
  weekCommencing: string;
  pots: OpenPotDto[];
}

/**
 * Narrows pots to the selected statuses. With every action status selected
 * (the default) all pots pass; an empty selection returns nothing.
 */
export function filterByStatuses(
  pots: OpenPotDto[],
  statuses: ActionPotStatus[]
): OpenPotDto[] {
  const selected = new Set<string>(statuses);
  return pots.filter(p => selected.has(p.status));
}

/** Groups pots by their period (oldest week first). Oldest first surfaces the
 * pots that have waited longest for an admin action. */
export function groupOpenPotsByWeek(pots: OpenPotDto[]): OpenPotsWeekGroup[] {
  const weeksByKey = new Map<string, OpenPotsWeekGroup>();

  for (const pot of pots) {
    const week = getOrCreateWeek(weeksByKey, pot);
    week.pots.push(pot);
  }

  return Array.from(weeksByKey.values()).sort(
    (a, b) =>
      a.periodYear - b.periodYear || a.periodWeekNumber - b.periodWeekNumber
  );
}

function getOrCreateWeek(
  weeksByKey: Map<string, OpenPotsWeekGroup>,
  pot: OpenPotDto
): OpenPotsWeekGroup {
  const key = `${pot.periodYear}-${pot.periodWeekNumber}`;
  const existing = weeksByKey.get(key);
  if (existing) {
    return existing;
  }

  const week: OpenPotsWeekGroup = {
    periodYear: pot.periodYear,
    periodWeekNumber: pot.periodWeekNumber,
    weekCommencing: pot.weekCommencing,
    pots: [],
  };
  weeksByKey.set(key, week);
  return week;
}
